// VAT-inclusive shop prices (UAE consumer rules: prices a shopper sees are
// shown including VAT), only while the store is VAT-registered.
//
// The catalogue, the cart and POST /orders keep prices **excluding** VAT; the
// server adds VAT on the order (backend utils/vat.py). Whether the store is
// registered, and the rate, come from GET /settings/public/store
// `vat_registered` / `vat_rate` (StoreFactsProvider, utils/storeDetails).
// A store that is not registered charges no VAT and never mentions it
// (owner, 6 Oct): rate 0, shop prices = catalogue prices.
// DEFAULT_VAT_RATE is 0: nothing adds VAT until the server (or its cached
// answer, utils/factsCache) says the store is registered.
// Every shop price goes through `withVat` here, never `* 1.05` in a component.

import { productPrices } from './productPricing';

export const DEFAULT_VAT_RATE = 0;

/** A usable rate (0 ≤ rate < 1) or the default. */
export const normalizeVatRate = (value) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) && n >= 0 && n < 1
    ? n
    : DEFAULT_VAT_RATE;
};

// Half-up to the fils like the server's Decimal rounding; toFixed(6) first
// drops float noise (550 * 1.05 = 577.4999… must give 577.50).
const round2 = (value) => Math.round(Number((Number(value) * 100).toFixed(6))) / 100;

/**
 * Ex-VAT amount → amount incl. VAT, rounded to the fils like an order
 * (utils/vat.calculate_vat: total = round2(taxable × (1 + rate))). The bag
 * total uses this on the goods after the coupon, so it equals the checkout.
 */
export const withVat = (amount, rate = DEFAULT_VAT_RATE) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return round2(n * (1 + normalizeVatRate(rate)));
};

/** Amount incl. VAT → ex-VAT (unrounded; for filters sent to the API). */
export const withoutVat = (amount, rate = DEFAULT_VAT_RATE) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return n / (1 + normalizeVatRate(rate));
};

/** "5" for 0.05 (for "Prices include 5% VAT"). */
export const vatPercent = (rate = DEFAULT_VAT_RATE) => String(round2(normalizeVatRate(rate) * 100));

/**
 * "1,234,567.5" style en-US digits without Intl (the first Intl formatter of
 * a page load costs tens of ms on a slow phone). `fraction`: 'keep2' always
 * shows 2 decimals, 'trim' drops trailing zeros (max 2 decimals).
 */
export const groupDigits = (amount, fraction = 'keep2') => {
  const n = round2(Number(amount) || 0);
  const negative = n < 0;
  let [whole, decimals] = Math.abs(n).toFixed(2).split('.');
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (fraction === 'trim') decimals = decimals.replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${decimals ? `.${decimals}` : ''}`;
};

/**
 * "1,250" or "577.50": whole dirhams without decimals, otherwise the fils.
 * Latin digits in both languages, like the rest of the shop.
 */
export const formatAmount = (amount) => {
  const n = round2(Number(amount) || 0);
  return Number.isInteger(n) ? groupDigits(n, 'trim') : groupDigits(n, 'keep2');
};

/** "1,250 AED". */
export const formatAed = (amount) => `${formatAmount(amount)} AED`;

/**
 * productPrices (utils/productPricing) with VAT added: what a shopper sees
 * on a card, the product page, search or the wishlist. `exVat` keeps the
 * server values (for the cart, analytics and delivery thresholds).
 */
export const shopperPrices = (product, rate = DEFAULT_VAT_RATE) => {
  const ex = productPrices(product);
  return {
    retail: withVat(ex.retail, rate),
    member: withVat(ex.member, rate),
    final: withVat(ex.final, rate),
    tier: ex.tier,
    exVat: ex,
  };
};

/**
 * A shopper's price filter bound (AED incl. VAT) as the ex-VAT value
 * GET /products `min_price` / `max_price` compare with. The minimum is
 * rounded down and the maximum up to the fils, so a piece shown at exactly
 * the bound stays in the list.
 */
export const priceBoundToApi = (amountInclVat, bound, rate = DEFAULT_VAT_RATE) => {
  const ex = withoutVat(amountInclVat, rate) * 100;
  return (bound === 'max' ? Math.ceil(ex - 1e-6) : Math.floor(ex + 1e-6)) / 100;
};

// ── Per-order VAT (documents decide from the ORDER, never today's facts) ──
// `vat_treatment` (POST /orders): "standard", "zero_rated_export" or
// "not_registered". Orders saved before the field existed were sold with VAT
// ("standard"), so they keep their VAT rows.

/** The order's VAT treatment ("standard" when the order predates the field). */
export const orderVatTreatment = (order) => {
  const v = order && typeof order === 'object' ? order.vat_treatment : null;
  return typeof v === 'string' && v ? v : 'standard';
};

/** False for an order sold while the store was not VAT-registered: no VAT row, no TRN, a plain "Invoice". */
export const orderHasVat = (order) => orderVatTreatment(order) !== 'not_registered';

/**
 * The order's VAT rate: its stored `vat_rate`, else worked out from its own
 * amounts (vat ÷ (total − vat), for orders saved without the rate), else 0.
 */
export const orderVatRate = (order) => {
  const o = order && typeof order === 'object' ? order : {};
  if (!orderHasVat(o)) return 0;
  const stored = Number(o.vat_rate);
  if (o.vat_rate !== null && o.vat_rate !== undefined && o.vat_rate !== '' && Number.isFinite(stored) && stored >= 0 && stored < 1) {
    return stored;
  }
  const vat = Number(o.vat_amount);
  const net = Number(o.total) - vat;
  return Number.isFinite(vat) && vat > 0 && Number.isFinite(net) && net > 0 ? Math.round((vat / net) * 1000) / 1000 : 0;
};

/** "5" / "0" / "2.5": the order's VAT percentage for labels. */
export const orderVatPercent = (order) => String(Math.round(orderVatRate(order) * 1000) / 10);

/**
 * The order's VAT row label ({en, ar}), or null when the order carries no
 * VAT at all (sold while not registered): callers then leave the row out.
 */
export const orderVatLabel = (order) => {
  if (!orderHasVat(order)) return null;
  if (orderVatTreatment(order) === 'zero_rated_export') {
    return { en: 'VAT (0%, export)', ar: 'ضريبة القيمة المضافة (0٪، تصدير)' };
  }
  const pct = orderVatPercent(order);
  return { en: `VAT (${pct}%)`, ar: `ضريبة القيمة المضافة (${pct}٪)` };
};
